/**
 * Maps between screen pixels and model units (feet).
 *
 * Kept pure and DOM-free so the coordinate math is unit-testable. The editor
 * holds one Viewport and consults it on every pointer event and render;
 * zooming and panning it lives in zoomPan.ts.
 */

import { Point } from '@kaiizen/kai-floor-plan';

export interface Viewport {
  /** Pixels per foot. Larger = more zoomed in. */
  scale: number;
  /** Screen-pixel offset of the model origin. */
  offset: Point;
}

export function createViewport(scale = 0.5): Viewport {
  return { scale, offset: { x: 0, y: 0 } };
}

/** Convert a screen-pixel point to model units. */
export function screenToModel(vp: Viewport, p: Point): Point {
  return {
    x: (p.x - vp.offset.x) / vp.scale,
    y: (p.y - vp.offset.y) / vp.scale,
  };
}

/** Convert a model-unit point to screen pixels. */
export function modelToScreen(vp: Viewport, p: Point): Point {
  return {
    x: p.x * vp.scale + vp.offset.x,
    y: p.y * vp.scale + vp.offset.y,
  };
}

/** Convert a model-unit length to screen pixels. */
export function modelToScreenLength(vp: Viewport, length: number): number {
  return length * vp.scale;
}

export function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}
